import { apiFetch } from '@/api/request'
import type {
  ApiResponse,
  VerificationPurpose,
  VerificationSendResult,
  VerificationVerifyResult,
} from '@/types/verification'

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const BASE = `${API_ORIGIN}/api/verification`

async function parseJson<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('API 서버에 연결할 수 없습니다. 백엔드(3002)와 프론트 dev 서버를 확인해 주세요.')
  }

  const data = (await response.json()) as ApiResponse<T>
  if (!response.ok && !data.message) {
    throw new Error('요청 처리에 실패했습니다.')
  }
  return data
}

export async function sendEmailVerification(email: string, purpose: VerificationPurpose) {
  const response = await apiFetch(`${BASE}/email/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, purpose }),
  })
  const result = await parseJson<VerificationSendResult>(response)
  if (!result.success) {
    const error = new Error(result.message || '이메일 인증번호 발송에 실패했습니다.') as Error & {
      waitSeconds?: number
    }
    if (result.data?.waitSeconds) error.waitSeconds = result.data.waitSeconds
    throw error
  }
  return result
}

export async function verifyEmailVerification(
  email: string,
  code: string,
  purpose: VerificationPurpose,
) {
  const response = await apiFetch(`${BASE}/email/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, purpose }),
  })
  const result = await parseJson<VerificationVerifyResult>(response)
  if (!result.success) {
    throw new Error(result.message || '이메일 인증에 실패했습니다.')
  }
  return result
}

export async function sendPhoneVerification(phone: string, purpose: VerificationPurpose) {
  const response = await apiFetch(`${BASE}/phone/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, purpose }),
  })
  const result = await parseJson<VerificationSendResult>(response)
  if (!result.success) {
    const error = new Error(result.message || '문자 인증번호 발송에 실패했습니다.') as Error & {
      waitSeconds?: number
    }
    if (result.data?.waitSeconds) error.waitSeconds = result.data.waitSeconds
    throw error
  }
  return result
}

export async function verifyPhoneVerification(
  phone: string,
  code: string,
  purpose: VerificationPurpose,
) {
  const response = await apiFetch(`${BASE}/phone/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, code, purpose }),
  })
  const result = await parseJson<VerificationVerifyResult>(response)
  if (!result.success) {
    throw new Error(result.message || '휴대폰 인증에 실패했습니다.')
  }
  return result
}
